/**
 * Drag state for the capacity move (CAP-4c / C18).
 *
 * Owned by `CapacityManagementUnified`, which also owns the `DndContext`, the
 * wizard instance and the sr-only live region. A context rather than props
 * because the drop targets sit four levels down (`CapacityViewColumn` →
 * `GroupCard`, `CapacityDetailSheet` → `CapacityCellRoster`, and the week
 * grid's `CalendarCell`), and threading drag state through every intermediate
 * component would add churn to files that have no interest in it.
 *
 * Consumers outside a provider get an inert default, so a component rendered
 * on a surface without drag-and-drop behaves exactly as it did before.
 */

import { createContext, useContext } from 'react';

import type { DraggedChild } from '@/hooks/useMoveDropTarget';

export interface MoveDragContextValue {
  /** The child currently being dragged, or null when no drag is active. */
  draggedChild: DraggedChild | null;
  /** The effective date a drop would use. */
  effectiveDate: string;
  /** False on surfaces where drag-and-drop is not wired up. */
  enabled: boolean;
}

const inertValue: MoveDragContextValue = {
  draggedChild: null,
  effectiveDate: '',
  enabled: false,
};

export const MoveDragContext = createContext<MoveDragContextValue>(inertValue);

export function useMoveDragContext(): MoveDragContextValue {
  return useContext(MoveDragContext);
}
